import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRealtimeStore } from '../../../shared/realtime/realtimeStore'
import { useCanOpenQueue } from '../../procurement/queueAccess'
import type { RosDashboardFilters, RosPeriodKey } from './types'

const PERIODS: { key: RosPeriodKey; label: string }[] = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
]

// Города — как в CityDropdown аккаунтов (Все / Санкт-Петербург / Санкт-Петербург 2 /
// Москва). Фильтр точный: «Санкт-Петербург» — только первый офис, второй — отдельно
// (решение Сергея 26.09.2026).
const CITIES = ['Санкт-Петербург', 'Санкт-Петербург 2', 'Москва']

function formatAgo(s: number): string {
  if (s < 60) return `${s} сек назад`
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`
  return `${Math.floor(s / 3600)} ч назад`
}

export function RosHeader({
  teamSize,
  period,
  onPeriodChange,
  fetchedAt,
  filters,
  onFiltersChange,
  team,
}: {
  teamSize: number
  period: RosPeriodKey
  onPeriodChange: (k: RosPeriodKey) => void
  fetchedAt: number | null
  filters: RosDashboardFilters
  onFiltersChange: (p: Partial<RosDashboardFilters>) => void
  team: { id: string; name: string }[]
}) {
  const wsStatus = useRealtimeStore((s) => s.status)
  // У ADMIN очередь закрыта (18.09) — кнопки «К очереди» у него нет (25.09).
  const canQueue = useCanOpenQueue()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const live = wsStatus === 'connected'
  const ago = fetchedAt ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : null

  const selectCls =
    'px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500'

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Дашборд РОЗ</h1>
          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
            <span>команда: {teamSize} закупщиков</span>
            <span>·</span>
            <span>матрицы: Китай + Производители</span>
            {ago !== null && (
              <>
                <span>·</span>
                <span>Обновлено {formatAgo(ago)}</span>
              </>
            )}
            <span>·</span>
            <span
              className={`inline-flex items-center gap-1 ${live ? 'text-emerald-600' : 'text-gray-400'}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300'}`}
              />
              {live ? 'Live' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => onPeriodChange(p.key)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  period === p.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {canQueue && (
            <Link
              to="/procurement/queue"
              className="px-3 py-1.5 rounded-xl bg-brand-600 text-white text-sm hover:bg-brand-700 transition-colors"
            >
              К очереди →
            </Link>
          )}
        </div>
      </div>

      {/* Фильтры: тип · город · закупщик */}
      <div className="flex flex-wrap gap-2 items-center bg-white rounded-xl border border-gray-200 px-3 py-2.5">
        <select
          value={filters.type}
          onChange={(e) => onFiltersChange({ type: e.target.value as RosDashboardFilters['type'] })}
          className={selectCls}
        >
          <option value="">Все типы</option>
          <option value="TEKUCHKA">Текучка</option>
          <option value="PROJECT">Проекты</option>
        </select>
        <select
          value={filters.city}
          onChange={(e) => onFiltersChange({ city: e.target.value })}
          className={selectCls}
        >
          <option value="">Все города</option>
          {CITIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filters.buyerId}
          onChange={(e) => onFiltersChange({ buyerId: e.target.value })}
          className={selectCls}
        >
          <option value="">Все закупщики</option>
          {team.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        {(filters.type || filters.city || filters.buyerId) && (
          <button
            onClick={() => onFiltersChange({ type: '', city: '', buyerId: '' })}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            × Сбросить
          </button>
        )}
      </div>
    </div>
  )
}
